import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BreakdownCost,
  BreakdownCostDetails,
  BreakdownCostType,
  ProfitLossResponse,
  useAsyncAction,
  useStores,
} from '../../hooks';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { Container, Drawer, Icon, Icons, Typography } from '../../stories/atoms';
import {
  ActionButton,
  Animation,
  Animations,
  Banner,
  BannerItemsType,
  Button,
  DropdownMenu,
  FilterDrawer,
  FilterType,
  IconWithText,
} from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { commonTranslations, homeTranslations, profitAndLossTransalations } from '../../translations';
import { getUniqueId, isFailureResponse } from '../../utils';
import { ContainerV2, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { Switch } from '../../stories/molecules/switch/Switch';
import { StyledButton, StyledDivider, StyledExpandButton, StyledPLCardContainer } from './ProfitAndLoss.styles';
import { ProfitLossChartSection } from './plChartSection/ProfitLossChartSection';
import { PLCardContent } from './plCardContent/PLCardContent';
import { ValueContainer } from './plCardContent/ValueContainer';
import { PLExpandCard } from './plExpandCard/PLExpandCard';
import PLFilter from './plFilter/PLFilter';
import {
  generateCardTitle,
  getFinancialYearDisplayLabel,
  getFinancialYearEndYear,
  getProfitLossFinancialDateConfig,
  profitLossPropertyToValueMap,
} from './util';
import { getProfitLossChartData } from './plChartSection/profitLossChartUtils';

export enum PLSortBy {
  SOB_ASC = 'sobAscending',
  SOB_DESC = 'sobDescending',
}

const detailCardOrder = ['shrinkage', 'scrapping', 'reductions', 'salaries', 'otherCosts', 'surplus1'];

const isProfitLossResponseEmpty = (profitLossData: ProfitLossResponse | null): boolean => {
  if (!profitLossData) return false;

  const hasSummaryData = [
    profitLossData.salesExcVat.actual,
    profitLossData.salesExcVat.budget ?? 0,
    profitLossData.salesExcVat.lastYear ?? 0,
    profitLossData.salesIncVat.actual,
    profitLossData.salesIncVat.budget ?? 0,
    profitLossData.salesIncVat.lastYear ?? 0,
  ].some((value) => value !== 0);

  const hasDetailsData = Object.values(profitLossData.details || {}).some((items) =>
    items.some((item) => (item.actual || 0) !== 0 || (item.budget || 0) !== 0 || (item.lastYear || 0) !== 0),
  );

  const hasMonthlyData = (profitLossData.monthlyBreakdown || []).some(
    (item) => (item.salesExcVat || 0) !== 0 || (item.expenses || 0) !== 0 || (item.surplus1 || 0) !== 0,
  );

  return !hasSummaryData && !hasDetailsData && !hasMonthlyData;
};

const getFetchMonthsAndKey = (
  selectedMonths: string[],
  monthsForSelectedYear: Record<string, string>,
): { fetchMonths: string[]; fetchKey: string } | null => {
  const availableMonthsForYear = Object.keys(monthsForSelectedYear);

  if (availableMonthsForYear.length === 0) {
    return null;
  }

  const selectedAvailableMonths = selectedMonths.filter((month) => availableMonthsForYear.includes(month));
  const fetchMonths = selectedAvailableMonths.length > 0 ? selectedAvailableMonths : availableMonthsForYear;
  const fetchKey = [...fetchMonths].sort().join(',');

  return { fetchMonths, fetchKey };
};

const getSelectedMonthlyBreakdown = (profitLossData: ProfitLossResponse | null, selectedMonths: string[]) => {
  const monthlyBreakdown = profitLossData?.monthlyBreakdown || [];

  if (!selectedMonths.length) {
    return monthlyBreakdown;
  }

  return monthlyBreakdown.filter((item) => selectedMonths.includes(item.month));
};

export const ProfitAndLoss: FC<unknown> = observer(() => {
  usePageEventTracker('profit_and_loss');

  const { t } = useTranslation();
  const { common } = homeTranslations;
  const { close } = commonTranslations;

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const {
    error,
    noData,
    headerText,
    loading: loadingTranslation,
    monthDropdown,
    yearDropdown,
    mobileBannerText,
    costNames,
    sortBy: sortByTranslations,
    chartProfitLoss,
    chartToggleShow,
    chartToggleHide,
    profitLossSummary,
    totalProfit,
    totalLoss,
    revenue,
    expenses,
  } = profitAndLossTransalations;
  const { sortBy: commonSortByTranslations } = commonTranslations;

  const { fetchProfitLossData } = useStores();

  const {
    result: profitLossDetails,
    isLoading: profitLossLoading,
    isError: profitLossError,
    triggerAction: triggerFetchProfitLossDetails,
  } = useAsyncAction<ProfitLossResponse>();

  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);
  const [expandData, setExpandData] = useState<Record<string, boolean>>({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerDetails, setDrawerDetails] = useState<BreakdownCostDetails | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [sortBy, setSortBy] = useState<PLSortBy | undefined>(undefined);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const isAnyExpanded = Object.values(expandData).some(Boolean);
  const [profitLossData, setProfitLossData] = useState<ProfitLossResponse | null>(null);

  const [showPLChart, setShowPLChart] = useState<boolean>(
    localStorage.getItem('showPLChart') === 'true' || localStorage.getItem('showPLChart') === null, // Default to true
  );

  const previousFetchKey = React.useRef<string>('');

  // Get financial year, months, and available years
  const { monthMap, availableYears, currentFinancialYear } = useMemo(() => getProfitLossFinancialDateConfig(), []);

  // Initialize selected year to current financial year
  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      const defaultYear = availableYears.includes(currentFinancialYear) ? currentFinancialYear : availableYears[0];
      setSelectedYear(defaultYear);
    }
  }, [availableYears, currentFinancialYear, selectedYear]);

  // Get months for selected financial year: December (previous year) - November (current year)
  const monthsForSelectedYear = useMemo(() => {
    if (!selectedYear) return {};
    const financialStartYear = Number.parseInt(selectedYear.split(' - ')[0]);
    const financialEndYear = financialStartYear + 1;
    const financialMonthKeys = [
      `${financialStartYear}12`,
      ...Array.from({ length: 11 }, (_, i) => `${financialEndYear}${String(i + 1).padStart(2, '0')}`),
    ];
    const yearMonths: Record<string, string> = {};
    financialMonthKeys.forEach((key) => {
      if (monthMap[key]) yearMonths[key] = monthMap[key];
    });
    return yearMonths;
  }, [selectedYear, monthMap]);

  // Calculate profit/loss summary for the selected year
  const profitLossSummaryData = useMemo(() => {
    if (!profitLossData || !selectedYear) return null;

    const monthlyBreakdown = getSelectedMonthlyBreakdown(profitLossData, selectedMonths);

    // Revenue is the sum of monthly sales excl. VAT.
    const revenueAmount = monthlyBreakdown.reduce((sum, item) => sum + (item.salesExcVat || 0), 0);

    // Expenses is the sum of all monthly expenses.
    const totalExpenses = monthlyBreakdown.reduce((sum, item) => sum + (item.expenses || 0), 0);

    // Net is the sum of monthly surplus (or derived sales-expenses when missing).
    const netAmount = monthlyBreakdown.reduce((sum, item) => {
      const monthlySales = item.salesExcVat || 0;
      const monthlyExpenses = item.expenses || 0;
      const monthlyNet = item.surplus1 ?? monthlySales - monthlyExpenses;

      return sum + monthlyNet;
    }, 0);

    const isProfit = netAmount >= 0;

    return {
      revenue: revenueAmount,
      totalExpenses,
      netAmount,
      isProfit,
    };
  }, [profitLossData, selectedMonths, selectedYear]);

  const profitLossValues =
    drawerDetails &&
    profitLossData &&
    profitLossPropertyToValueMap(drawerDetails.name, drawerDetails, profitLossData?.salesExcVat);

  // Fetch profit and loss data
  const fetchProfitLoss = useCallback(
    async (monthArray: string[]) => {
      const response = await fetchProfitLossData(monthArray);

      if (isFailureResponse(response)) {
        throw new Error();
      }
      return response.data;
    },
    [fetchProfitLossData],
  );

  const profitLossChartData = useMemo(() => {
    return getProfitLossChartData(
      profitLossData,
      selectedMonths,
      monthsForSelectedYear,
      t(chartProfitLoss.key, chartProfitLoss.defaultValue),
    );
  }, [
    profitLossData,
    selectedMonths,
    monthsForSelectedYear,
    t,
    chartProfitLoss.key,
    chartProfitLoss.defaultValue,
  ]);

  // Handle accordion expand
  const handleAccordionExpand = (value: { [key in string]: boolean }) => {
    setExpandData((prev) => ({
      ...prev,
      ...value,
    }));
  };

  const getIsExpanded = (key: string) => {
    return expandData[key] ?? false;
  };

  // Handle expand all button
  const handleAllExpanded = () => {
    if (!profitLossData) return;
    const updatedExpandData: Record<string, boolean> = {};

    Object.keys(profitLossData.details).forEach((item) => {
      updatedExpandData[item] = true;
    });

    setExpandData(updatedExpandData);
  };

  const handleAllCollapse = () => {
    setExpandData({});
  };

  // Handle details drawer
  const onViewDetailsClick = (details: BreakdownCostDetails) => {
    setIsDrawerOpen(true);
    setDrawerDetails(details);
  };

  const getTitle = (costName: string) => {
    const title = costNames[costName as BreakdownCostType]
      ? t(costNames[costName as BreakdownCostType].key, costNames[costName as BreakdownCostType].defaultValue)
      : costName;

    return generateCardTitle('', title, false);
  };

  const handleFilterReset = useCallback(() => {
    if (selectedMonths.length) setSelectedMonths([]);
    setSortBy(undefined);
  }, [selectedMonths]);

  const handleFilterClose = useCallback(
    (selectedMonth: string, filterType: string) => {
      if (filterType === 'month') {
        const filteredMonths = selectedMonths.filter((month) => month !== selectedMonth);
        setSelectedMonths(filteredMonths);
      } else {
        setSortBy(undefined);
      }
    },
    [selectedMonths],
  );

  // Trigger fetch profit and loss details on year/month filter changes
  useEffect(() => {
    const fetchConfig = getFetchMonthsAndKey(selectedMonths, monthsForSelectedYear);
    if (!fetchConfig) return;

    if (previousFetchKey.current === fetchConfig.fetchKey) return;

    previousFetchKey.current = fetchConfig.fetchKey;
    triggerFetchProfitLossDetails(() => fetchProfitLoss(fetchConfig.fetchMonths));
  }, [selectedMonths, monthsForSelectedYear, triggerFetchProfitLossDetails, fetchProfitLoss]);

  // Reset selected months when year changes
  useEffect(() => {
    if (selectedYear) {
      setSelectedMonths([]);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (profitLossDetails) {
      setProfitLossData(profitLossDetails);
    }
  }, [profitLossDetails]);

  // Set banner items for mobile
  useEffect(() => {
    if (isMobile) {
      setBannerItems([
        {
          id: getUniqueId(),
          label: t(mobileBannerText.key, mobileBannerText.defaultValue),
          type: 'info',
          actionButton: ActionButton.Close,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('showPLChart', JSON.stringify(showPLChart));
  }, [showPLChart]);

  const sortedProfitLossData = React.useMemo(() => {
    if (!profitLossData) return null;
    if (!sortBy) return profitLossData;
    const sortedDetails = Object.entries(profitLossData.details).reduce(
      (acc, [category, items]) => ({
        ...acc,
        [category]: [...items].sort((a, b) =>
          sortBy === PLSortBy.SOB_ASC ? a.actual - b.actual : b.actual - a.actual,
        ),
      }),
      {} as BreakdownCost,
    );

    return { ...profitLossData, details: sortedDetails };
  }, [sortBy, profitLossData]);

  const orderedDetailEntries = useMemo(() => {
    if (!sortedProfitLossData) {
      return [] as Array<[string, BreakdownCostDetails[]]>;
    }

    const detailEntries = Object.entries(sortedProfitLossData.details) as Array<[string, BreakdownCostDetails[]]>;
    const detailEntryMap = new Map(detailEntries);
    const prioritizedEntries = detailCardOrder
      .map((costName) => {
        const content = detailEntryMap.get(costName);

        return content ? ([costName, content] as [string, BreakdownCostDetails[]]) : null;
      })
      .filter((entry): entry is [string, BreakdownCostDetails[]] => entry !== null);
    const remainingEntries = detailEntries.filter(([costName]) => !detailCardOrder.includes(costName));

    return [...prioritizedEntries, ...remainingEntries];
  }, [sortedProfitLossData]);

  const isNoData = useMemo(() => {
    if (!profitLossData) {
      return false;
    }

    const selectedMonthlyBreakdown = getSelectedMonthlyBreakdown(profitLossData, selectedMonths);

    if (selectedMonths.length > 0 && selectedMonthlyBreakdown.length === 0) {
      return true;
    }

    // Prefer selected-month data for no-data detection.
    if (selectedMonthlyBreakdown.length > 0) {
      const hasSelectedMonthlyData = selectedMonthlyBreakdown.some(
        (item) => (item.salesExcVat || 0) !== 0 || (item.expenses || 0) !== 0 || (item.surplus1 || 0) !== 0,
      );

      return !hasSelectedMonthlyData;
    }

    return isProfitLossResponseEmpty(profitLossData);
  }, [profitLossData, selectedMonths]);

  return (
    <Page
      header={
        <Container direction="horizontal" space="between" padding={isMobile ? [0, 4] : [2, 8]}>
          {isMobile && (
            <>
              <Banner items={bannerItems} onChange={() => null} />
              <FilterDrawer
                isOpen={isFilterDrawerOpen}
                onDrawerClose={() => setIsFilterDrawerOpen(false)}
                onClearAllFilters={handleFilterReset}
                filtersSpecs={[
                  {
                    key: 'year',
                    type: FilterType.RadioList,
                    title: t(yearDropdown.key, yearDropdown.defaultValue),
                    options: availableYears.map((year) => ({
                      key: year,
                      title: getFinancialYearEndYear(year),
                      disabled: false,
                    })),
                    selectedOptionKey: selectedYear ?? null,
                    onSelect: (selectedItemKey) => setSelectedYear(selectedItemKey),
                  },
                  {
                    key: 'month',
                    type: FilterType.MultiSelectList,
                    title: t(monthDropdown.key, monthDropdown.defaultValue),
                    options: Object.entries(monthsForSelectedYear)
                      .reverse()
                      .map(([key, value]) => ({
                        key: key,
                        title: value,
                        disabled: false,
                      })),
                    selectedOptionsKeys: selectedMonths,
                    onSelect: (selectedItemKeys) => setSelectedMonths(selectedItemKeys),
                  },
                  {
                    key: 'sortBy',
                    type: FilterType.RadioList,
                    title: t(commonSortByTranslations.key, commonSortByTranslations.defaultValue),
                    options: [
                      {
                        key: PLSortBy.SOB_ASC,
                        title: t(
                          sortByTranslations.sobAscending.key,
                          sortByTranslations.sobAscending.defaultValue,
                        ),
                      },
                      {
                        key: PLSortBy.SOB_DESC,
                        title: t(
                          sortByTranslations.sobDescending.key,
                          sortByTranslations.sobDescending.defaultValue,
                        ),
                      },
                    ],
                    selectedOptionKey: sortBy ?? null,
                    onSelect: (selectedItemKey) => setSortBy(selectedItemKey as PLSortBy),
                  },
                ]}
              />
              <Button
                label={t(commonTranslations.allFilters.key, commonTranslations.allFilters.defaultValue)}
                buttonContentProps={{
                  labelPosition: 'left',
                  iconOptions: {
                    icon: Icons.FILTER,
                    size: 'small',
                  },
                  textOptions: {
                    whiteSpace: 'nowrap',
                  },
                }}
                aria-haspopup="true"
                aria-expanded={isFilterDrawerOpen ? 'true' : undefined}
                onClick={() => setIsFilterDrawerOpen(true)}
                variant="text"
              />
            </>
          )}
          {isMobile && drawerDetails && (
            <Drawer
              variant="temporary"
              position="bottom"
              isOpen={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              hasBorderRadius
            >
              <Container direction="horizontal" wrap="nowrap" padding={[6, 8, 2, 6]} space="between">
                <Typography type="h3">{getTitle(drawerDetails.name)}</Typography>
                <IconWithText
                  label=""
                  iconOptions={{
                    icon: Icons.CLOSE,
                    size: 'small',
                  }}
                  onClick={() => setIsDrawerOpen(false)}
                />
              </Container>
              <Container wrap="nowrap" padding={[2, 8, 4]}>
                {profitLossValues &&
                  profitLossValues.map((item) => (
                    <React.Fragment key={item.key}>
                      <ValueContainer
                        costName={drawerDetails.name}
                        label={item.key}
                        value={item.value?.toLocaleString()}
                      />
                    </React.Fragment>
                  ))}
                <StyledButton
                  label={t(close.key, close.defaultValue)}
                  data-testid="close-drawer"
                  textOptions={{
                    type: 'button',
                    color: 'white',
                  }}
                  margin={[3, 0, 3]}
                  hasRadius
                  onClick={() => setIsDrawerOpen(false)}
                />
              </Container>
            </Drawer>
          )}

          {!isMobile && (
            <Container wrap="nowrap" direction="horizontal" style={{ gap: '28px' }} margin={[0, 0, 3, 0]}>
              <DropdownMenu
                data-testid="select-year-dropdown"
                dropdownLabel={t(yearDropdown.key, yearDropdown.defaultValue)}
                menuItems={availableYears.map((year) => ({
                  key: year,
                  item: <Typography margin={[0, 2]}>{getFinancialYearEndYear(year)}</Typography>,
                }))}
                minWidth={'0px'}
                isCompact
                buttonContentProps={{
                  isDisabled: profitLossLoading || profitLossError,
                }}
                maxHeight={'50vh'}
                initialSelected={selectedYear ? [selectedYear] : undefined}
                onSelect={(selectedItems) => {
                  setSelectedYear(selectedItems[0]);
                }}
                isOnlyIcon
                allowTooltip
              />
              <DropdownMenu
                data-testid="select-month-dropdown"
                dropdownLabel={t(monthDropdown.key, monthDropdown.defaultValue)}
                menuItems={Object.entries(monthsForSelectedYear)
                  .reverse()
                  .map(([key, value]) => ({
                    key: key,
                    item: <Typography margin={[0, 2]}>{value}</Typography>,
                  }))}
                minWidth={'0px'}
                multiSelect
                checkbox
                isCompact
                buttonContentProps={{
                  isDisabled: profitLossLoading || profitLossError,
                }}
                maxHeight={'50vh'}
                initialSelected={selectedMonths}
                onSelect={(selectedItems) => {
                  setSelectedMonths(selectedItems);
                }}
                isOnlyIcon
                allowTooltip
              />
              <DropdownMenu
                data-testid="sort-dropdown"
                dropdownLabel={t(commonSortByTranslations.key, commonSortByTranslations.defaultValue)}
                menuItems={[
                  {
                    key: PLSortBy.SOB_ASC,
                    item: (
                      <Typography margin={[0, 2]}>
                        {t(sortByTranslations.sobAscending.key, sortByTranslations.sobAscending.defaultValue)}
                      </Typography>
                    ),
                  },
                  {
                    key: PLSortBy.SOB_DESC,
                    item: (
                      <Typography margin={[0, 2]}>
                        {t(sortByTranslations.sobDescending.key, sortByTranslations.sobDescending.defaultValue)}
                      </Typography>
                    ),
                  },
                ]}
                minWidth={'0px'}
                isCompact
                buttonContentProps={{
                  isDisabled: profitLossLoading || profitLossError,
                }}
                maxHeight={'50vh'}
                initialSelected={sortBy ? [sortBy] : undefined}
                onSelect={(selectedItems) => {
                  setSortBy(selectedItems[0] as PLSortBy);
                }}
                isOnlyIcon
                allowTooltip
              />
            </Container>
          )}

          <StyledExpandButton
            data-testid="expand-button"
            label={
              isAnyExpanded
                ? t(common.collapseAll.key, common.collapseAll.defaultValue)
                : t(common.expandAll.key, common.expandAll.defaultValue)
            }
            shouldRotate={isAnyExpanded ? false : true}
            iconOptions={{
              icon: isAnyExpanded ? Icons.CHECKBOX_INDETERMINATE : Icons.CLOSE,
              size: 'small',
              rotate: 'up',
            }}
            textOptions={{
              type: 'button',
            }}
            onClick={isAnyExpanded ? handleAllCollapse : handleAllExpanded}
          />
        </Container>
      }
      body={
        <div>
          {profitLossLoading && (
            <Animation
              title={t(loadingTranslation.key, loadingTranslation.defaultValue)}
              animation={Animations.LOADING}
            />
          )}

          {!profitLossLoading && profitLossError && (
            <Animation
              title={t(error.errorTitle.key, error.errorTitle.defaultValue)}
              subtitle={t(error.errorSubtitle.key, error.errorSubtitle.defaultValue)}
              animation={Animations.SAD}
            />
          )}

          {!profitLossLoading && !profitLossError && profitLossDetails && isNoData && (
            <Container wrap="nowrap" padding={isMobile ? [3, 4] : [4, 8]}>
              {!isMobile && (
                <PLFilter
                  selectedYear={selectedYear}
                  selectedMonths={selectedMonths}
                  sortBy={sortBy}
                  monthMap={monthsForSelectedYear}
                  onClose={handleFilterClose}
                  onReset={handleFilterReset}
                />
              )}
              <Animation
                title={t(noData.noDataTitle.key, noData.noDataTitle.defaultValue)}
                subtitle={t(noData.noDataSubtitle.key, noData.noDataSubtitle.defaultValue)}
                animation={Animations.CONFUSED}
              />
            </Container>
          )}

          {!profitLossLoading && !profitLossError && profitLossDetails && !isNoData && (
            <Container
              wrap="nowrap"
              padding={isMobile ? [3, 4] : [4, 8]}
              data-testid="profit-and-loss-page-wrapper"
            >
              <ContainerV2 justifyContent="between" margin={[0, 0, 3, 0]}>
                <RowFlexContainer alignItems="center" justifyContent="between">
                  <Typography gutterBottom>
                    {t(headerText.key, {
                      defaultValue: headerText.defaultValue,
                      reportPeriod: getFinancialYearEndYear(selectedYear),
                    })}
                  </Typography>

                  <Switch
                    data-testid="pl-chart-switch"
                    isActive={showPLChart}
                    label={
                      showPLChart
                        ? t(chartToggleHide.key, chartToggleHide.defaultValue)
                        : t(chartToggleShow.key, chartToggleShow.defaultValue)
                    }
                    labelPosition="left"
                    labelType="body3"
                    onClick={() => {
                      setShowPLChart(!showPLChart);
                    }}
                  />
                </RowFlexContainer>
                {(selectedMonths.length > 0 || sortBy) && !isMobile && (
                  <PLFilter
                    selectedMonths={selectedMonths}
                    sortBy={sortBy}
                    monthMap={monthsForSelectedYear}
                    onClose={handleFilterClose}
                    onReset={handleFilterReset}
                  />
                )}
              </ContainerV2>

              <ProfitLossChartSection
                showPLChart={showPLChart}
                selectedYear={selectedYear}
                financialYear={selectedYear}
                profitLossChartData={profitLossChartData}
                isMobile={isMobile}
              />

              {profitLossSummaryData && (
                <StyledPLCardContainer
                  isParent
                  padding={[3]}
                  margin={[0, 0, 4, 0]}
                  data-testid="profit-loss-summary-container"
                >
                  <Typography type="h4" margin={[0, 0, 3, 0]} data-testid="profit-loss-summary-title">
                    {t(profitLossSummary.key, profitLossSummary.defaultValue)} -{' '}
                    {getFinancialYearDisplayLabel(selectedYear)}
                  </Typography>

                  <Container direction="horizontal" space="between" wrap="wrap">
                    <Container direction="horizontal" wrap="nowrap" position="center">
                      <Icon icon={Icons.TAG} size={isMobile ? 'small' : 'medium'} margin={[1, 2]} />
                      <Container>
                        <Typography type="body2" margin={[0, 0, 1, 0]}>
                          {t(revenue.key, revenue.defaultValue)}: {profitLossSummaryData.revenue.toLocaleString()}
                        </Typography>
                        <Typography type="body2" margin={[0, 0, 1, 0]}>
                          {t(expenses.key, expenses.defaultValue)}:{' '}
                          {profitLossSummaryData.totalExpenses.toLocaleString()}
                        </Typography>
                      </Container>
                    </Container>

                    <Container direction="horizontal" wrap="nowrap" position="center">
                      <Icon
                        icon={profitLossSummaryData.isProfit ? Icons.TRENDUP : Icons.TRENDDOWN}
                        size={isMobile ? 'small' : 'medium'}
                        color={profitLossSummaryData.isProfit ? 'validationGreen' : 'red'}
                        margin={[1, 2]}
                      />
                      <Container>
                        <Typography
                          type="body3"
                          color={profitLossSummaryData.isProfit ? 'validationGreen' : 'red'}
                          data-testid="net-profit-loss-amount"
                        >
                          {profitLossSummaryData.isProfit
                            ? t(totalProfit.key, totalProfit.defaultValue)
                            : t(totalLoss.key, totalLoss.defaultValue)}
                          : {Math.abs(profitLossSummaryData.netAmount).toLocaleString()}
                        </Typography>
                      </Container>
                    </Container>
                  </Container>
                </StyledPLCardContainer>
              )}

              <StyledPLCardContainer
                padding={isMobile ? [3, 2] : [4, 3]}
                margin={[0, 0, 4, 0]}
                isParent
                position="bottom"
                data-testid="profit-loss-summary-card"
              >
                <Container position="center" wrap="nowrap" direction="horizontal" width="100%">
                  <Icon icon={Icons.TAG} size={isMobile ? 'small' : 'medium'} margin={[1, 2]} />
                  <PLCardContent
                    costDetails={profitLossDetails.salesIncVat}
                    compareCostDetails={profitLossDetails.salesIncVat}
                    onViewDetailsClick={onViewDetailsClick}
                    costName="salesIncVat"
                    isParent
                  />
                </Container>
                <StyledDivider />
                <Container position="center" wrap="nowrap" direction="horizontal" width="100%">
                  <Icon icon={Icons.TAG} size={isMobile ? 'small' : 'medium'} margin={[1, 2]} />
                  <PLCardContent
                    costDetails={profitLossDetails.salesExcVat}
                    compareCostDetails={profitLossDetails.salesExcVat}
                    onViewDetailsClick={onViewDetailsClick}
                    costName="salesExcVat"
                    isParent
                  />
                </Container>
              </StyledPLCardContainer>

              {sortedProfitLossData &&
                orderedDetailEntries.map(([parentCost, content]) => (
                  <PLExpandCard
                    key={parentCost}
                    costName={parentCost}
                    compareCostDetails={sortedProfitLossData.salesExcVat}
                    content={content}
                    isParent
                    isExpanded={getIsExpanded(parentCost)}
                    onViewDetailsClick={onViewDetailsClick}
                    onExpand={handleAccordionExpand}
                  />
                ))}
            </Container>
          )}
        </div>
      }
    />
  );
});
